import { api, ensureApiAuthenticated } from '../services/api';

export type ChannelName = 'shopify' | 'manapool' | 'tcgplayer';
export type ChannelStatus = 'active' | 'inactive' | 'error' | 'syncing' | 'connected' | 'degraded' | 'disconnected';

export interface Channel {
  id: string;
  name: ChannelName;
  display_name: string;
  status?: ChannelStatus;
  sync_enabled: boolean;
  inbound_inventory_sync_enabled: boolean;
  outbound_inventory_sync_enabled: boolean;
  orders_webhook_enabled: boolean;
  credentials_vault_path?: string | null;
  last_sync_at?: string | null;
  last_error?: string | null;
}

export interface ChannelLogEntry {
  id: string;
  channel_id: string;
  /** Null when the source payload had no usable instant. */
  timestamp: string | null;
  action: string;
  status: 'success' | 'error' | 'info' | 'warning';
  level?: 'success' | 'error' | 'info' | 'warning';
  message: string;
  details?: Record<string, unknown>;
}

export interface ChannelListing {
  id: string;
  channel_id: string;
  variant_id: string;
  external_id?: string | null;
  price?: string | null;
  quantity?: string | null;
  status: 'pending' | 'synced' | 'failed';
  last_error?: string | null;
  updated_at?: string | null;
}

export interface ChannelsListResponse {
  items: Channel[];
  total: number;
}

export interface ListingsListResponse {
  items: ChannelListing[];
  total: number;
}

export interface TriggerSyncRequest {
  variant_ids?: string[];
  sync_all_in_stock?: boolean;
  limit?: number;
  /** Resume full-catalog backfill after this variant id (exclusive). */
  after_variant_id?: string;
  dry_run?: boolean;
  /** Manapool inbound recon only: detect | suggest | apply */
  policy_mode?: 'detect' | 'suggest' | 'apply';
}

export interface TriggerSyncResponse {
  success_count: number;
  failed_count: number;
  total: number;
  errors: Array<{ variant_id: string; error: string }>;
  accepted?: boolean;
  message?: string;
  policy_mode?: string;
  next_after_variant_id?: string | null;
  has_more?: boolean;
  after_variant_id?: string | null;
  batch_limit?: number | null;
}

interface LegacyTriggerSyncResponse {
  status?: string;
  message?: string;
  synced?: number;
  failed?: number;
  errors?: Array<{ variant_id?: string; error?: string } | string>;
}

interface InventoryManapoolSyncResponse {
  status?: string;
  message?: string;
  copies_deleted?: number;
  copies_created?: number;
  copies_unchanged?: number;
  policy_mode?: string;
}

export interface ManapoolInventorySyncStatus {
  status: 'idle' | 'running' | 'success' | 'failed';
  phase: string;
  message: string;
  progress_percent: number;
  fetched_items: number;
  remote_total_items?: number | null;
  processed_variants: number;
  total_variants: number;
  copies_created: number;
  copies_deleted: number;
  copies_unchanged: number;
  started_at?: string | null;
  finished_at?: string | null;
  last_error?: string | null;
  policy_mode?: string | null;
  status_backend?: 'redis' | 'memory' | string;
  shared?: boolean;
}

export interface PollManapoolOrdersRequest {
  limit?: number;
  max_fetches?: number;
}

export interface PollManapoolOrdersResponse {
  status: string;
  orders_fetched: number;
  orders_queued: number;
  orders_skipped: number;
  batches_fetched: number;
  limit: number;
  max_fetches: number;
  actor?: string;
}

export interface RefreshManapoolOrderStatusesRequest {
  order_ids?: string[];
  limit?: number;
}

export interface RefreshManapoolOrderStatusesResponse {
  status: string;
  actor?: string;
  queued_count: number;
  candidate_count: number;
  mode: 'explicit_order_ids' | 'pending_orders';
  limit: number;
  pending_statuses: string[];
  processed_count?: number;
  success_count?: number;
  error_count?: number;
  results?: Array<{
    status: string;
    order_id?: string;
    external_id?: string;
    error?: string;
    items_processed?: number;
    commercial_status?: string;
    fulfillment_status?: string;
    source_status?: string;
    manapool_status?: string;
    seller_list_status?: string;
  }>;
}

export interface ChannelFailureSummary {
  error: string;
  count: number;
  last_seen_at?: string | null;
}

export interface ChannelRuntimeReport {
  channel_name: ChannelName;
  generated_at?: string | null;
  sync_in_progress: boolean;
  inferred_status: 'active' | 'idle' | 'error';
  listing_totals: {
    synced: number;
    pending: number;
    failed: number;
    total: number;
  };
  recent_activity: {
    updated_last_1m: number;
    updated_last_5m: number;
    updated_last_15m: number;
  };
  throughput_per_min_last_5m: number;
  last_listing_update_at?: string | null;
  recent_failures: ChannelFailureSummary[];
}

export interface ChannelProfile {
  id: string;
  display_name: string;
  description: string;
}

export interface ApplyChannelProfileRequest {
  enable_manapool_inbound_reconciliation?: boolean;
  dry_run?: boolean;
}

export interface ApplyChannelProfileResponse {
  profile_id: string;
  display_name: string;
  dry_run: boolean;
  message: string;
  channels: Channel[];
}

export async function listChannels(): Promise<ChannelsListResponse> {
  await ensureApiAuthenticated();
  const response = await api.get('/channels');
  const payload = response.data;

  if (Array.isArray(payload)) {
    return { items: payload as Channel[], total: payload.length };
  }

  const items = Array.isArray(payload?.items) ? (payload.items as Channel[]) : [];
  const total = typeof payload?.total === 'number' ? payload.total : items.length;

  return { items, total };
}

export async function listChannelProfiles(): Promise<ChannelProfile[]> {
  await ensureApiAuthenticated();
  const response = await api.get('/channels/profiles');
  return response.data as ChannelProfile[];
}

export async function applyChannelProfile(
  profileId: string,
  request: ApplyChannelProfileRequest = {}
): Promise<ApplyChannelProfileResponse> {
  await ensureApiAuthenticated();
  const response = await api.post(`/channels/profiles/${profileId}/apply`, request);
  return response.data as ApplyChannelProfileResponse;
}

export async function listChannelListings(channel: ChannelName): Promise<ListingsListResponse> {
  await ensureApiAuthenticated();
  const response = await api.get(`/channels/${channel}/listings`);
  const payload = response.data;

  if (Array.isArray(payload)) {
    return { items: payload as ChannelListing[], total: payload.length };
  }

  const items = Array.isArray(payload?.items) ? (payload.items as ChannelListing[]) : [];
  const total = typeof payload?.total === 'number' ? payload.total : items.length;
  return { items, total };
}

export async function fetchChannelReport(channel: ChannelName): Promise<ChannelRuntimeReport> {
  await ensureApiAuthenticated();
  const response = await api.get(`/channels/${channel}/report`);
  return response.data as ChannelRuntimeReport;
}

export async function updateChannel(
  channel: ChannelName,
  updates: Partial<Pick<Channel, 'sync_enabled' | 'inbound_inventory_sync_enabled' | 'outbound_inventory_sync_enabled' | 'orders_webhook_enabled' | 'credentials_vault_path'>>
): Promise<Channel> {
  await ensureApiAuthenticated();
  const response = await api.patch(`/channels/${channel}`, updates);
  return response.data as Channel;
}

export async function pollManapoolOrders(
  options: PollManapoolOrdersRequest = {}
): Promise<PollManapoolOrdersResponse> {
  await ensureApiAuthenticated();
  const response = await api.post('/orders/manapool/poll', undefined, {
    params: {
      ...(typeof options.limit === 'number' ? { limit: options.limit } : {}),
      ...(typeof options.max_fetches === 'number' ? { max_fetches: options.max_fetches } : {}),
    },
  });
  return response.data as PollManapoolOrdersResponse;
}

export async function refreshManapoolOrderStatuses(
  options: RefreshManapoolOrderStatusesRequest = {}
): Promise<RefreshManapoolOrderStatusesResponse> {
  await ensureApiAuthenticated();
  const payload = {
    ...(Array.isArray(options.order_ids) ? { order_ids: options.order_ids } : {}),
    ...(typeof options.limit === 'number' ? { limit: options.limit } : {}),
  };
  const response = await api.post('/orders/manapool/refresh-status', payload);
  return response.data as RefreshManapoolOrderStatusesResponse;
}

export interface ShopifyFullSyncStatus {
  status: string;
  phase: string;
  message: string;
  batches_completed: number;
  success_count: number;
  failed_count: number;
  total: number;
  started_at?: string | null;
  finished_at?: string | null;
  last_error?: string | null;
}

export async function fetchShopifyFullSyncStatus(): Promise<ShopifyFullSyncStatus> {
  await ensureApiAuthenticated();
  const response = await api.get<ShopifyFullSyncStatus>('/channels/shopify/sync/status');
  return response.data;
}

export async function triggerSync(
  channel: ChannelName,
  payload?: TriggerSyncRequest
): Promise<TriggerSyncResponse> {
  await ensureApiAuthenticated();

  // Keep Manapool behavior aligned with the inventory page flow.
  if (channel === 'manapool') {
    // Manapool reconciliation can take longer than default API timeout.
    // Default to detect (read-only) — apply must be explicit from the UI.
    const policyMode = payload?.policy_mode ?? 'detect';
    const response = await api.post('/inventory/manapool/sync', undefined, {
      timeout: 10 * 60 * 1000,
      params: { policy_mode: policyMode },
    });
    const data = response.data as InventoryManapoolSyncResponse;
    const deleted = typeof data.copies_deleted === 'number' ? data.copies_deleted : 0;
    const created = typeof data.copies_created === 'number' ? data.copies_created : 0;
    const unchanged = typeof data.copies_unchanged === 'number' ? data.copies_unchanged : 0;
    const total = deleted + created + unchanged;

    return {
      success_count: total,
      failed_count: 0,
      total,
      errors: [],
      accepted: false,
      message: data.message,
      policy_mode: data.policy_mode ?? policyMode,
    };
  }

  const hasPayload =
    payload !== undefined &&
    Object.values(payload).some((value) => value !== undefined && value !== null);

  const response = hasPayload
    ? await api.post(`/channels/${channel}/sync`, payload, { timeout: 15 * 60 * 1000 })
    : await api.post(`/channels/${channel}/sync`, undefined, { timeout: 15 * 60 * 1000 });

  const data = response.data as TriggerSyncResponse | LegacyTriggerSyncResponse;

  if (
    typeof (data as TriggerSyncResponse).success_count === 'number' &&
    typeof (data as TriggerSyncResponse).failed_count === 'number'
  ) {
    const typed = data as TriggerSyncResponse;
    return {
      ...typed,
      accepted: Boolean(typed.accepted),
    };
  }

  const legacy = data as LegacyTriggerSyncResponse;
  const successCount = typeof legacy.synced === 'number' ? legacy.synced : 0;
  const failedCount = typeof legacy.failed === 'number' ? legacy.failed : 0;
  const errors = Array.isArray(legacy.errors)
    ? legacy.errors.map((entry) => {
        if (typeof entry === 'string') {
          return { variant_id: 'unknown', error: entry };
        }
        return {
          variant_id: entry.variant_id ?? 'unknown',
          error: entry.error ?? 'Unknown error',
        };
      })
    : [];

  return {
    success_count: successCount,
    failed_count: failedCount,
    total: successCount + failedCount,
    errors,
    accepted: true,
    message: legacy.message,
  };
}

export async function fetchManapoolInventorySyncStatus(): Promise<ManapoolInventorySyncStatus> {
  await ensureApiAuthenticated();
  const response = await api.get('/inventory/manapool/sync/status');
  return response.data as ManapoolInventorySyncStatus;
}
